// `/settings/style` — Settings → My style (W7): the strategist's emails and answers in, their
// own voice profile out. Scout's chat runs the same steps ("set up my email style").
//
// Five steps on one page, with a progress strip and one main action at a time (P4-08): paste
// emails, answer what Scout still asks for in boxes right under its asks, build, preview three
// drafts, use it. Every answer from the server is the whole state, so each step replaces the
// page's copy. Offered with the Scout tab; hidden otherwise.

import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AccessDeniedError, approveStyle, compileStyle, editStyleRules, fetchMe, fetchStyle, panelMessage, previewStyle,
  readViewAs, saveStyleIntake,
} from '../api/client';
import type { StyleRulesRequest, StyleState } from '../api/types';
import { useAuthContext } from '../auth/AuthContext';
import { DeniedGate } from '../auth/Gate';
import { PageChrome } from '../components/PageChrome';
import { SectionHead } from '../components/SectionHead';
import { StyleCoveragePanel } from '../components/style/StyleCoveragePanel';
import { StyleIntakeForm } from '../components/style/StyleIntakeForm';
import { StyleProfilePanel } from '../components/style/StyleProfilePanel';
import { StyleProgress } from '../components/style/StyleProgress';
import { formFromState, formWithAdditions, intakeRequest, profileStatus, styleStage } from '../lib/styleIntake';
import type { InlineAdditions, StyleForm } from '../lib/styleIntake';

const STYLE_QUERY_KEY = ['style'];

export function StyleView() {
  const { getToken, email, signOut } = useAuthContext();
  const queryClient = useQueryClient();
  const meQuery = useQuery({ queryKey: ['me'], queryFn: () => fetchMe(getToken) });
  const styleQuery = useQuery({ queryKey: STYLE_QUERY_KEY, queryFn: () => fetchStyle(getToken) });
  const [form, setForm] = useState<StyleForm | null>(null);
  const [busyStep, setBusyStep] = useState<string | null>(null);
  const [errorText, setErrorText] = useState('');
  const viewer = meQuery.data?.viewer;
  const viewingAs = readViewAs();

  // One runner for every step: show the busy step, replace the state, keep the error on screen.
  // Says whether the step worked, so an inline box clears only once its text is saved.
  async function run(step: string, call: () => Promise<StyleState>): Promise<boolean> {
    setBusyStep(step);
    setErrorText('');
    try {
      const next = await call();
      queryClient.setQueryData(STYLE_QUERY_KEY, next);
      if (step === 'check') setForm(formFromState(next));
      return true;
    } catch (error) {
      setErrorText(panelMessage(error, 'That step failed.'));
      return false;
    } finally {
      setBusyStep(null);
    }
  }

  // A rule change is a new version; draft fresh previews in it straight away.
  async function editRulesAndPreview(changes: StyleRulesRequest): Promise<StyleState> {
    const edited = await editStyleRules(getToken, changes);
    return previewStyle(getToken, edited.latest_version);
  }

  if (meQuery.error instanceof AccessDeniedError) {
    return <DeniedGate message={meQuery.error.message} email={email} onSignOut={signOut} />;
  }
  if (meQuery.isPending || styleQuery.isPending) {
    return <div className="loading">Loading your email style…</div>;
  }
  if (viewer && !viewer.sees_scout_tab) {
    return <Navigate to={viewer.default_view === 'execution' ? '/execution' : '/activity'} replace />;
  }

  const state = styleQuery.data;
  const currentForm = form ?? formFromState(state);
  const profile = state?.latest_profile ?? null;
  const stage = styleStage(state);
  const saved = Boolean(state && state.emails.length);

  // Boxes under Scout's asks: merged into the form and saved as one replace, like the form itself.
  function addFromAsks(additions: InlineAdditions): Promise<boolean> {
    const next = formWithAdditions(currentForm, additions);
    return run('check', () => saveStyleIntake(getToken, intakeRequest(next)));
  }

  return (
    <PageChrome freshness={null} viewer={viewer}>
      <div className="stylepage">
        <SectionHead title="My email style" />
        <p className="stylelede">
          Paste a few emails you sent and liked, and Scout drafts the way you write. About five
          minutes. You can also do this by chatting with Scout.
        </p>
        <StyleProgress stage={stage} />
        <p className="stylestatus">{profileStatus(state)}</p>
        {viewingAs ? (
          <p className="stylenote">
            You are acting as {viewingAs}. Changes here edit their style and are recorded as yours.
          </p>
        ) : null}
        {state?.notice ? <p className="stylenote">{state.notice}</p> : null}
        {errorText ? <div className="err">{errorText}</div> : null}

        <StyleIntakeForm
          form={currentForm}
          emails={state?.emails ?? []}
          onChange={setForm}
          busy={busyStep === 'check'}
          saved={saved}
          onCheck={() => run('check', () => saveStyleIntake(getToken, intakeRequest(currentForm)))}
        />

        {/* Before a profile exists, Scout's asks are the next thing to do; after, they sit below it. */}
        {state && saved && !profile ? (
          <StyleCoveragePanel
            coverage={state.coverage}
            busyStep={busyStep}
            hasProfile={false}
            onAdd={addFromAsks}
            onBuild={(allowGaps) => run('compile', () => compileStyle(getToken, allowGaps))}
          />
        ) : null}

        {state && profile ? (
          <StyleProfilePanel
            state={state}
            profile={profile}
            busyStep={busyStep}
            onPreview={() => run('preview', () => previewStyle(getToken, profile.version))}
            onApprove={(version) => run('approve', () => approveStyle(getToken, version))}
            onEditRules={(changes: StyleRulesRequest) => run('rules', () => editRulesAndPreview(changes))}
          />
        ) : null}

        {state && saved && profile ? (
          <details className="styleoptional stylerebuild">
            <summary>Add more emails and rebuild</summary>
            <StyleCoveragePanel
              coverage={state.coverage}
              busyStep={busyStep}
              hasProfile
              onAdd={addFromAsks}
              onBuild={(allowGaps) => run('compile', () => compileStyle(getToken, allowGaps))}
            />
          </details>
        ) : null}
      </div>
    </PageChrome>
  );
}
